import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectCredits } from '@/utils/store';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import CurrencyInput from '@/components/ui/CurrencyInput';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';
import { useFormDraft } from '@/hooks/useFormDraft';

const ICON_OPTIONS = ['🏢', '🏠', '🏡', '🏘️', '🏗️', '🏬', '🏛️', '🏰', '🏗️', '🏚️', '🛖', '🏕️'];
const COLOR_OPTIONS = ['#8b5cf6', '#3b82f6', '#ec4899', '#f59e0b', '#10b981', '#f97316'];

export default function AddPropertyModal({ open, onClose, property = null }) {
  const { t } = useTranslation();
  const addProperty = useStore((s) => s.addProperty);
  const updateProperty = useStore((s) => s.updateProperty);
  const credits = useStore(selectCredits);
  const banks = useStore((s) => s.banks);
  const isEdit = !!property;
  const draft = useFormDraft('addProperty');

  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [icon, setIcon] = useState('🏢');
  const [color, setColor] = useState('#8b5cf6');
  const [creditId, setCreditId] = useState('');
  const [rentAmount, setRentAmount] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [tenantContact, setTenantContact] = useState('');
  const [memberIds, setMemberIds] = useState([]);
  const [status, setStatus] = useState('available');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && property) {
      setName(property.name || '');
      setAddress(property.address || '');
      setIcon(property.icon || '🏢');
      setColor(property.color || '#8b5cf6');
      setCreditId(property.creditId || '');
      setRentAmount(String(property.rentAmount || ''));
      setTenantName(property.tenantName || '');
      setTenantContact(property.tenantContact || '');
      setMemberIds(property.memberIds || []);
      setStatus(property.status || 'available');
      setErrors({});
    } else if (open) {
      const saved = draft.load();
      if (saved) {
        setName(saved.name || '');
        setAddress(saved.address || '');
        setIcon(saved.icon || '🏢');
        setColor(saved.color || '#8b5cf6');
        setCreditId(saved.creditId || '');
        setRentAmount(saved.rentAmount || '');
        setTenantName(saved.tenantName || '');
        setTenantContact(saved.tenantContact || '');
        setMemberIds(saved.memberIds || []);
        setStatus(saved.status || 'available');
      } else {
        setName('');
        setAddress('');
        setIcon('🏢');
        setColor('#8b5cf6');
        setCreditId('');
        setRentAmount('');
        setTenantName('');
        setTenantContact('');
        setMemberIds([]);
        setStatus('available');
      }
      setErrors({});
    }
  }, [open, property]);

  // Auto-save draft
  useEffect(() => {
    if (!open || isEdit) return;
    draft.save({ name, address, icon, color, creditId, rentAmount, tenantName, tenantContact, memberIds, status });
  }, [open, isEdit, name, address, icon, color, creditId, rentAmount, tenantName, tenantContact, memberIds, status]);

  const handleSave = () => {
    const errs = {};
    if (!name.trim()) errs.name = t('properties.form.nameRequired');
    if (!rentAmount || Number(rentAmount) <= 0) errs.rentAmount = t('properties.form.rentRequired');
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const data = {
      name: name.trim(),
      address: address.trim(),
      icon,
      color,
      creditId: creditId || undefined,
      rentAmount: Number(rentAmount),
      tenantName: tenantName.trim(),
      tenantContact: tenantContact.trim(),
      memberIds,
      status,
    };

    if (isEdit) {
      updateProperty(property.id, data);
    } else {
      addProperty(data);
    }
    draft.clear();
    onClose();
  };

  const getBankName = (bankId) => {
    const bank = banks.find((b) => b.id === bankId);
    return bank ? bank.name : '';
  };

  return (
    <Modal open={open} onClose={onClose} title={isEdit ? t('properties.editProperty') : t('properties.addNew')} footer={
      <Button className="w-full" onClick={handleSave}>
        {t('common.save')}
      </Button>
    }>
      <div className="space-y-4">
        {/* Name */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('properties.form.name')}
          </label>
          <input
            type="text"
            className={`input ${errors.name ? 'border-danger-500' : ''}`}
            placeholder={t('properties.form.namePlaceholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <p className="text-xs text-danger-500 mt-1">{errors.name}</p>}
        </div>

        {/* Address */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('properties.form.address')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('properties.form.addressPlaceholder')}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </div>

        {/* Icon */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('properties.form.icon')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {ICON_OPTIONS.map((ic) => (
              <button
                key={ic}
                onClick={() => setIcon(ic)}
                className={`w-10 h-10 rounded-xl text-lg flex items-center justify-center border-2 transition-all ${
                  icon === ic
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 scale-110'
                    : 'border-gray-200 dark:border-gray-700 hover:border-brand-400'
                }`}
              >
                {ic}
              </button>
            ))}
          </div>
        </div>

        {/* Color */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('properties.form.color')}
          </label>
          <div className="flex gap-2">
            {COLOR_OPTIONS.map((c) => (
              <button
                key={c}
                onClick={() => setColor(c)}
                className={`w-9 h-9 rounded-full transition-all ${
                  color === c ? 'ring-2 ring-offset-2 ring-brand-500 dark:ring-offset-gray-900 scale-110' : ''
                }`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('properties.form.status')}
          </label>
          <div className="flex gap-2 flex-wrap">
            {['available', 'rented', 'maintenance'].map((s) => (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  status === s
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
                }`}
              >
                {t(`properties.form.status${s.charAt(0).toUpperCase() + s.slice(1)}`)}
              </button>
            ))}
          </div>
        </div>

        {/* Rent amount */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('properties.form.rentAmount')}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-bold text-gray-300">$</span>
            <CurrencyInput
              placeholder="0"
              value={rentAmount}
              onChange={setRentAmount}
              className={`w-full pl-10 pr-4 py-3 text-lg font-bold input ${errors.rentAmount ? 'border-danger-500' : ''}`}
            />
          </div>
          {errors.rentAmount && <p className="text-xs text-danger-500 mt-1">{errors.rentAmount}</p>}
        </div>

        {/* Mortgage credit */}
        {credits.length > 0 && (
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
              {t('properties.form.credit')}
            </label>
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setCreditId('')}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                  !creditId
                    ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                    : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
                }`}
              >
                {t('properties.form.noCredit')}
              </button>
              {credits.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCreditId(c.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    creditId === c.id
                      ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                      : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                  }`}
                >
                  {c.description}
                  {c.bankId && (
                    <span className="text-gray-400">({getBankName(c.bankId)})</span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Owner / Members */}
        <MemberMultiSelect
          value={memberIds}
          onChange={setMemberIds}
          label={t('properties.form.owner')}
        />

        {/* Tenant name */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('properties.form.tenantName')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('properties.form.tenantNamePlaceholder')}
            value={tenantName}
            onChange={(e) => setTenantName(e.target.value)}
          />
        </div>

        {/* Tenant contact */}
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
            {t('properties.form.tenantContact')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('properties.form.tenantContactPlaceholder')}
            value={tenantContact}
            onChange={(e) => setTenantContact(e.target.value)}
          />
        </div>

      </div>
    </Modal>
  );
}
